import {
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  TablePagination,
  Typography,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import axios, { AxiosResponse } from 'axios';
import { isMobile, isTablet, isDesktop } from 'react-device-detect';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { PaginationContext } from '../../../contexts/pagination-context';
import { useSnackBar } from '../../../contexts/snackbar';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { PickupTaskState } from '../../../enums/tasks';
import { useApiConfigWithBranch } from '../../../hooks/use-api-config-with-branch';
import { IPickupTask } from '../../../interfaces/delivery-company/tasks';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import CustomDialog from '../../common/custom-dialog/custom-dialog';
import DoubleDatePicker from '../../common/double-date-picker/double-date-picker';
import { DoubleDate } from '../../common/double-date-picker/double-date-picker.interfaces';
import { ErrorsFlattenner } from '../../common/errors-flattenner/errors-flattenner';
import FilterBar from '../../common/filter-bar/filter-bar';
import { buildQueryParams } from '../../common/filter-bar/filter-bar.utils';
import { IFilterOption } from '../../common/filter-bar/types';
import ResponsivePickupTasksListing from '../common/pickup-tasks-listing';
import CreatePickupRequest from './create-task-form/create-pickup-task-form';
import PickupTaskDialog from './pickup-task-dialog';
import ReceiveOrdersDialog from './receive-orders-dialog/receive-orders-dialog';
import { ExpandMore, ManageSearch } from '@mui/icons-material';
import {
  DataResources,
  ResourceActionType,
  user_has_data_model_access,
} from '../../../utils/permissions';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import PickupTaskWaybillsDialog from '../../common/pickup-task-waybills-dialog';
import { useBranchFilter } from '../../../contexts/branch-filter-context';
import BranchFilterSelector from '../../common/branch-filter-selector/branch-filter-selector';

function PickupTasks() {
  const [tasks, setTasks] = useState<IPickupTask[]>([]);
  const [tasksCount, setTasksCount] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showWaybillsDialogFor, setShowWaybillsDialogFor] = useState<IPickupTask | null>(null);
  const [editingTask, setEditingTask] = useState<IPickupTask>();
  const [cancellingPickupRequest, setCancellingPickupRequest] =
    useState<IPickupTask>();
  const [response, setResponse] = useState<AxiosResponse>();
  const [receiveOrdersForTask, setReceiveOrdersForTask] =
    useState<IPickupTask>();
  const [message, setMessage] = useState<string>('');
  const [filters, setFilters] = useState<IFilterOption[]>([]);
  const [dateFilter, setDateFilter] = useState<DoubleDate>({
    startDate: null,
    endDate: null,
  });
  const [creatingNewTask, setCreatingNewTask] = useState(false);
  const { showMessage } = useSnackBar();

  const { user } = useContext(AuthContext);
  const { config, branchKey } = useApiConfigWithBranch();
  const { showAllBranches } = useBranchFilter();

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
  });

  const fetchTasks = async () => {
      console.log('triggered by controller');
      setIsLoading(true);
      const relevant_fields = [
        'id',
        'status',
        'package_count',
        'workflow',
        'creation_date',
        'created_by',
        'customer',
        'driver',
        'merchant',
        'merchant_location',
        'customer_location',
        'is_critical',
        'branch'
      ];

      console.log(relevant_fields);

      let endpoint = `${API_ENDPOINT}/api/v1/pickup-tasks/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&fields=${relevant_fields.join(',')}`;

      const filterQueryParams = buildQueryParams(filters);
      endpoint += `${filterQueryParams}`;

      const { startDate, endDate } = dateFilter;

      if (startDate !== null) {
        endpoint += `&date_from=${encodeURIComponent(startDate.format('YYYY-MM-DD'))}`;
      }

      if (endDate !== null) {
        endpoint += `&date_to=${encodeURIComponent(endDate.format('YYYY-MM-DD'))}`;
      }

      if (showAllBranches) {
        endpoint += `&all_branches=true`;
      }

      try {
        console.log(endpoint);
        const response = await axios.get<{
          count: number;
          results: IPickupTask[];
        }>(endpoint, config);
        setResponse(response);
        setTasks(response.data.results);
        setTasksCount(response.data.count);
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    };


  useEffect(() => {
    fetchTasks();
  }, [JSON.stringify(controller), JSON.stringify(filters), dateFilter, branchKey, showAllBranches]);

  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
    });
  };

  const handleCancelTask = async () => {
    try {
      setMessage('');
      setResponse(undefined);
      setIsLoading(true);
      const endpoint = `${API_ENDPOINT}/api/v1/pickup-task/${cancellingPickupRequest?.id}/`;
      const response = await axios.delete(endpoint, config);
      setResponse(response);
      setTasks(
        tasks.map((task) =>
          task.id === cancellingPickupRequest?.id
            ? { ...task, status: PickupTaskState.CANCELLED }
            : task
        )
      );
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage(
          'Failed to cancel pickup request: ' + e.response?.statusText
        );
      } else {
        setMessage('Failed to cancel pickup request');
      }
    }
    setIsLoading(false);
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const dateFilterAction = (dateFilterValue: DoubleDate) => {
    resetPaginationControls();
    setDateFilter(dateFilterValue);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationControls();
    setFilters(filters);
  };

  return (
    <div>
      <Box>
        <Typography
          variant="h3"
          component="h3"
          sx={{ textAlign: 'center', mt: 3, mb: 1 }}
        >
          Pickup Requests
        </Typography>

        <BranchFilterSelector />

        <br />

        {
          <Grid container spacing={2}>
            <Grid
              xs={12}
              sx={{
                textAlign: 'right',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <Button
                size={(isMobile && !isTablet) ? 'small' : 'medium'}
                onClick={() => setCreatingNewTask(true)}
                variant="contained"
                disabled={!user_has_data_model_access(user, DataResources.PICKUP_TASK, ResourceActionType.CREATE)}
              >
                New Pickup Request
              </Button>
            </Grid>
            <Grid xs={12}>
              <Box>
                <Accordion elevation={0}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <Typography
                      variant="h5"
                      component="h5"
                      sx={{ textAlign: 'center', mt: 1, mb: 1 }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                        }}
                      >
                        <ManageSearch fontSize="large" color="primary" />
                        <span>
                          <b>Search and Filter Controls</b>
                        </span>
                      </div>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DoubleDatePicker dateFilterAction={dateFilterAction} />
                    <br />
                    <FilterBar
                      filterQueryAction={filterQueryAction}
                      currentlySelectedFilters={filters}
                      filterKeys={[
                        'driver',
                        'customer',
                        'merchant',
                        'pickup task state',
                        'district',
                        'workflow',
                      ]}
                    />
                  </AccordionDetails>
                </Accordion>

                {isLoading ? (
                  <LinearProgress />
                ) : (
                  <>
                    {message != '' && (
                      <>
                        <Alert
                          severity={
                            response &&
                            response.status >= 200 &&
                            response.status < 300
                              ? 'success'
                              : 'error'
                          }
                        >
                          <>
                            {message}

                            {response && response.status >= 300 && (
                              <ErrorsFlattenner rawErrors={response.data} />
                            )}
                          </>
                        </Alert>
                        <br />{' '}
                      </>
                    )}

                    <div style={{ width: '100%', textAlign: 'center' }}>
                      <Chip
                        color="primary"
                        label={`${tasksCount} Pickups`}
                        variant={'outlined'}
                        sx={{ padding: 1 }}
                      />
                    </div>

                    <ResponsivePickupTasksListing
                      tasksList={tasks}
                      isLoading={isLoading}
                      setEditingTask={setEditingTask}
                      setReceiveOrdersForTask={setReceiveOrdersForTask}
                      setCancellingPickupRequest={setCancellingPickupRequest}
                      setDownloadWaybillForTask={(task: IPickupTask) => {
                        setShowWaybillsDialogFor(task);
                      }}
                    />

                    <TablePagination
                      component="div"
                      onPageChange={handlePageChange}
                      page={controller.page}
                      count={tasksCount}
                      rowsPerPage={controller.rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[
                        5, 10, 25, 50, 100, 200, 250, 500, 1000,
                      ]}
                      labelRowsPerPage=""
                      style={{ overflow: 'visible' }}
                    />
                  </>
                )}

                {showWaybillsDialogFor && (
                  <PickupTaskWaybillsDialog
                    pickupTaskId={showWaybillsDialogFor.id}
                    onClose={() => setShowWaybillsDialogFor(null)}
                  />
                )}
                {receiveOrdersForTask && (
                  <ReceiveOrdersDialog
                    task={receiveOrdersForTask}
                    onClose={() => setReceiveOrdersForTask(undefined)}
                  />
                )}
                {editingTask && (
                  <PickupTaskDialog
                    taskID={editingTask.id}
                    onClose={() => setEditingTask(undefined)}
                  />
                )}

                <ConfirmAction
                  message={'Are you sure you want to cancel this order?'}
                  action={handleCancelTask}
                  isOpen={cancellingPickupRequest != undefined}
                  handleClose={() => setCancellingPickupRequest(undefined)}
                  isSevereAction={true}
                  confirmSevereActionTarget={'cancel request'}
                >
                  <Alert severity="warning">
                    This action can not be undone.
                  </Alert>
                </ConfirmAction>
              </Box>
            </Grid>
          </Grid>
        }
      </Box>

      <CustomDialog
        open={creatingNewTask}
        fullWidth
        maxWidth="xl"
        fullScreen={(isMobile && !isTablet)}
        onClose={() => {
          setCreatingNewTask(false);
        }}
      >
        <div
          style={{
            textAlign: 'right',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <IconButton
            onClick={() => {
              setCreatingNewTask(false);
            }}
          >
            <ClearIcon color="primary" fontSize="large" />
          </IconButton>
        </div>

        <DialogContent
          style={(isDesktop || isTablet) ? { width: '100%', height: '100%' } : {}}
        >
          <CreatePickupRequest
            onSuccess={(p, is_newly_created) => {
              const message = is_newly_created
                ? `Pickup #${p.id} created`
                : `Updated Existing Pickup #${p.id}`;
              showMessage(message);
              fetchTasks()
              setCreatingNewTask(false);
            }}
          />
        </DialogContent>
      </CustomDialog>
    </div>
  );
}

export default PickupTasks;